import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";

const CrosshairTooltip = ({
  stageRef,
  isVisible,
  onClose,
  secondaryMessage = "",
}) => {
  const [position, setPosition] = useState({
    left: 0,
    top: 0,
    placeRightOfCanvas: false,
  });

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const updatePosition = () => {
      const mainCanvasContainer = document.getElementById(
        "main-canvas-stage-container",
      );
      if (!mainCanvasContainer) {
        return;
      }
      const mainCanvasRect = mainCanvasContainer.getBoundingClientRect();
      if (!mainCanvasRect || mainCanvasRect.width <= 0) {
        return;
      }

      // Measure actual tooltip size from DOM (fallback values avoid NaN during first paint)
      const tooltipElement = document.querySelector("[data-crosshair-tooltip]");
      let tooltipWidth = 200;
      let tooltipHeight = 90;
      if (tooltipElement) {
        const tooltipRect = tooltipElement.getBoundingClientRect();
        tooltipWidth = tooltipRect.width || 200;
        tooltipHeight = tooltipRect.height || 90;
      }

      const arrowOffset = 10;
      const viewportPadding = 12;
      const leftCandidate = mainCanvasRect.left - tooltipWidth - arrowOffset;
      const rightCandidate = mainCanvasRect.right + arrowOffset;
      const placeRightOfCanvas = leftCandidate < viewportPadding;
      const preferredLeft = placeRightOfCanvas ? rightCandidate : leftCandidate;
      const maxLeft = Math.max(
        viewportPadding,
        window.innerWidth - tooltipWidth - viewportPadding,
      );
      const clampedLeft = Math.min(
        Math.max(preferredLeft, viewportPadding),
        maxLeft,
      );
      const preferredTop = mainCanvasRect.top + 16;
      const maxTop = Math.max(
        viewportPadding,
        window.innerHeight - tooltipHeight - viewportPadding,
      );
      const clampedTop = Math.min(
        Math.max(preferredTop, viewportPadding),
        maxTop,
      );

      const newPosition = {
        left: clampedLeft,
        top: clampedTop,
        placeRightOfCanvas,
      };
      setPosition((prev) => {
        if (
          prev.left === newPosition.left &&
          prev.top === newPosition.top &&
          prev.placeRightOfCanvas === newPosition.placeRightOfCanvas
        ) {
          return prev;
        }
        return newPosition;
      });
    };

    updatePosition();
    const timeoutId = setTimeout(updatePosition, 100);
    const intervalId = setInterval(updatePosition, 200);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isVisible, stageRef]);

  if (!isVisible) {
    return null;
  }

  const tooltipContent = (
    <div
      data-crosshair-tooltip
      data-canvas-chrome
      style={{
        position: "fixed",
        left: `${position.left}px`,
        top: `${position.top}px`,
        zIndex: 10000,
        opacity: position.left === 0 && position.top === 0 ? 0 : 1,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
        }}
      >
        <div
          style={{
            position: "relative",
            backgroundColor: "#000000",
            color: "#FFFFFF",
            padding: "6px 10px",
            borderRadius: "4px",
            fontSize: "13px",
            fontFamily: "sans-serif",
            display: "flex",
            alignItems: "flex-start",
            gap: "0px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            maxWidth: "160px",
          }}
        >
          <span style={{ lineHeight: "1.4", flex: 1, marginRight: "-4px" }}>
            Click anywhere on the Canvas to duplicate.
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              if (onClose) {
                onClose();
              }
            }}
            style={{
              flexShrink: 0,
              marginTop: "2px",
              marginLeft: "-4px",
              background: "transparent",
              border: "none",
              color: "#FFFFFF",
              cursor: "pointer",
              padding: "0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              lineHeight: "1",
              width: "20px",
              height: "20px",
            }}
            aria-label="Close tooltip"
          >
            ×
          </button>
          <div
            style={{
              position: "absolute",
              right: position.placeRightOfCanvas ? "auto" : "-8px",
              left: position.placeRightOfCanvas ? "-8px" : "auto",
              top: "50%",
              transform: "translateY(-50%)",
              width: 0,
              height: 0,
              borderTop: "8px solid transparent",
              borderBottom: "8px solid transparent",
              borderLeft: position.placeRightOfCanvas
                ? "none"
                : "8px solid #000000",
              borderRight: position.placeRightOfCanvas
                ? "8px solid #000000"
                : "none",
            }}
          />
        </div>
        {secondaryMessage && (
          <div
            style={{
              position: "relative",
              backgroundColor: "#111111",
              color: "#FFE082",
              padding: "6px 10px",
              borderRadius: "4px",
              fontSize: "12px",
              fontFamily: "sans-serif",
              maxWidth: "200px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
            }}
          >
            <span style={{ lineHeight: "1.4" }}>{secondaryMessage}</span>
          </div>
        )}
      </div>
    </div>
  );

  // Render to document body using portal to ensure it stays mounted
  return ReactDOM.createPortal(tooltipContent, document.body);
};

CrosshairTooltip.propTypes = {
  stageRef: PropTypes.object,
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  secondaryMessage: PropTypes.string,
};

export default CrosshairTooltip;
